import React, { useState, useEffect, useRef } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuthContext } from '@/contexts/AuthContext';
import { usePermissions } from '@/contexts/PermissionContext';
import { MODULE_NAMES } from '@/constants/modules';
import { supabase } from '@/lib/supabase';
import Index from '@/pages/Index';
import HomePage from '@/pages/HomePage';

import Subscriptions from '@/pages/Subscriptions';
import SubscriptionDetail from '@/pages/SubscriptionDetail';
import SubscriptionItemDetail from '@/pages/SubscriptionItemDetail';
import InvoiceDetail from '@/pages/InvoiceDetail';
import Customers from '@/pages/Customers';
import CustomerDetail from '@/pages/CustomerDetail';
import Vendors from '@/pages/Vendors';
import VendorForm from '@/pages/VendorForm';
import VendorDetail from '@/pages/VendorDetail';
import Users from '@/pages/Users';
import NotFound from '@/pages/NotFound';
import Login from '@/pages/auth/Login';

import Callback from '@/pages/auth/Callback';
import SubscriptionPlans from '@/pages/SubscriptionPlans';
import EditSubscriptionPlan from '@/pages/subscription-plans/EditSubscriptionPlan';
import NewSubscriptionPlan from '@/pages/subscription-plans/NewSubscriptionPlan';
import SubscriptionPlanDetails from '@/pages/subscription-plans/SubscriptionPlanDetails';
import SubscriptionPlanFamilies from '@/pages/SubscriptionPlanFamilies';
import Profile from '@/pages/Profile';
import Settings from '@/pages/Settings';
import SubscriptionWizardPage from '@/pages/SubscriptionWizardPage';
import SubscriptionConfirmation from '@/components/subscription-wizard/SubscriptionConfirmation';
import Integrations from '@/pages/Integrations';
import Courses from '@/pages/Courses';
import CourseForm from '@/pages/CourseForm';

import CourseFamilies from '@/pages/CourseFamilies';
import Classes from '@/pages/Classes';
import ClassSessions from '@/pages/ClassSessions';
import SessionsCalendar from '@/pages/SessionsCalendar';
import ClassSponsors from '@/pages/ClassSponsors';
import Instructors from '@/pages/Instructors';
import ClassDetails from '@/pages/ClassDetails';
import ClassWizardPage from '@/pages/ClassWizardPage';
import ClassConfirmationPage from '@/pages/ClassConfirmationPage';
import CourseDetail from '@/pages/CourseDetail';
import ClassesDashboard from '@/pages/ClassesDashboard';
import UnifiedDashboard from '@/pages/UnifiedDashboard';
import TransactionalEmails from '@/pages/TransactionalEmails';
import EmailTemplateCreate from '@/pages/EmailTemplateCreate';
import EmailTemplateEdit from '@/pages/EmailTemplateEdit';
import ReactEmailPreview from '@/pages/ReactEmailPreview';
import EmailPreviewV4 from '@/pages/email-preview-v4';
import AccessControl from '@/pages/AccessControl';

import Unauthorized from '@/pages/Unauthorized';
import SystemJobs from '@/pages/SystemJobs';
import SystemOrders from '@/pages/SystemOrders';
import SubscriptionWizardStep1 from '@/pages/subscriptions/wizard/Step1';
import Invoices from '@/pages/Invoices';
import EconomicIndicesPage from '@/pages/subscriptions/EconomicIndices';
import ImportSubscriptionsPlans from '@/pages/ImportSubscriptionsPlans';
import TaskManagementPage from '@/pages/task-management/index';
import TaskSettingsPage from '@/pages/task-management/settings';
import TaskLabelsPage from '@/pages/task-management/labels';
import Transactions from '@/pages/Transactions';
import TransactionDetail from '@/pages/TransactionDetail';
import VoucherCodes from '@/pages/VoucherCodes';
import MaximizedLayout from '@/components/layout/MaximizedLayout';
import DashboardLayout from '@/components/layout/DashboardLayout';
import BusinessOpportunities from '@/pages/BusinessOpportunities';
import BusinessOpportunityForm from '@/pages/BusinessOpportunityForm';
import BusinessOpportunityDetail from '@/pages/BusinessOpportunityDetail';
import OpportunityDetailSimple from '@/pages/OpportunityDetailSimple';
import ProposalDetail from '@/pages/ProposalDetail';
import PublicProposalView from '@/pages/PublicProposalView';
import OpportunityWizardPage from '@/pages/OpportunityWizardPage';
import OpportunityConfirmation from '@/components/opportunity-wizard/OpportunityConfirmation';
import ProductCatalog from '@/pages/ProductCatalog';
import ProductCatalogDetails from '@/pages/ProductCatalogDetails';

import ContractDetail from '@/pages/ContractDetail';
import Contracts from '@/pages/Contracts';
import ContractTemplates from '@/pages/ContractTemplates';
import ContractTemplateForm from '@/pages/ContractTemplateForm';
import ProposalTemplates from '@/pages/ProposalTemplates';
import ProposalTemplateForm from '@/pages/ProposalTemplateForm';
import PlaybookPublicView from '@/pages/customer-success/playbooks/PlaybookPublicView';
import PlaybooksCustomerSuccessPage from '@/pages/customer-success/playbooks/Playbooks';
import EditPlaybookTemplate from '@/pages/customer-success/playbooks/EditPlaybookTemplate';
import NewPlaybookTemplate from '@/pages/customer-success/playbooks/NewPlaybookTemplate';
import PlaybookRunDetail from '@/pages/customer-success/playbooks/PlaybookRunDetail';
import PlaybookRuns from '@/pages/customer-success/playbooks/PlaybookRuns';
import NewPlaybookRun from '@/pages/customer-success/playbooks/NewPlaybookRun';
import SystemNotifications from '@/pages/SystemNotifications';
// Import support pages
import SupportInbox from '@/pages/SupportInbox';
import SupportTickets from '@/pages/SupportTickets';
import LoadingExamples from '@/pages/loading-examples';
import MAUAnalytics from '@/pages/analytics/MAUAnalytics';
import ProtectedRoute from '@/components/ProtectedRoute'; // Importar o componente unificado

function AppRoutes() {
  const { user, loading } = useAuthContext();

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={<MaximizedLayout><Login /></MaximizedLayout>} />
      <Route path="/auth/callback" element={<Callback />} />
      
      {/* New sessions calendar */}
      <Route path="/sessions-calendar" element={
        <ProtectedRoute requiredModule={MODULE_NAMES.CLASSES}>
          <DashboardLayout>
            <SessionsCalendar />
          </DashboardLayout>
        </ProtectedRoute>
      } />

      {/* Public playbook view for clients */}
      <Route path="/p/:id/:token" element={<PlaybookPublicView />} />

      {/* Public proposal views */}
      <Route path="/public-proposal/:token" element={<PublicProposalView />} />

      {/* Support routes */}
      <Route
        path="/support/inbox"
        element={
          <ProtectedRoute requiredModule={MODULE_NAMES.SETTINGS}>
            <DashboardLayout>
              <SupportInbox />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/support/tickets"
        element={
          <ProtectedRoute requiredModule={MODULE_NAMES.SETTINGS}>
            <DashboardLayout>
              <SupportTickets />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      {/* Protected routes */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <HomePage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute requiredModule={MODULE_NAMES.DASHBOARD}>
            <DashboardLayout>
              <UnifiedDashboard />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/customers"
        element={
          <ProtectedRoute requiredModule={MODULE_NAMES.CUSTOMERS}>
            <DashboardLayout>
              <Customers />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/customers/:id"
        element={
          <ProtectedRoute requiredModule={MODULE_NAMES.CUSTOMERS}>
            <DashboardLayout>
              <CustomerDetail />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/vendors"
        element={
          <ProtectedRoute requiredModule={MODULE_NAMES.VENDORS}>
            <DashboardLayout>
              <Vendors />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/vendors/new"
        element={
          <ProtectedRoute requiredModule={MODULE_NAMES.VENDORS}>
            <DashboardLayout>
              <VendorForm />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/vendors/:id"
        element={
          <ProtectedRoute requiredModule={MODULE_NAMES.VENDORS}>
            <DashboardLayout>
              <VendorDetail />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/vendors/:id/edit"
        element={
          <ProtectedRoute requiredModule={MODULE_NAMES.VENDORS}>
            <DashboardLayout>
              <VendorForm />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/subscriptions"
        element={
          <ProtectedRoute requiredModule={MODULE_NAMES.SUBSCRIPTIONS}>
            <DashboardLayout>
              <Subscriptions />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/import-subscriptions-plans"
        element={
          <ProtectedRoute requiredModule={MODULE_NAMES.SUBSCRIPTIONS}>
            <DashboardLayout>
              <ImportSubscriptionsPlans />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/subscriptions/:id"
        element={
          <ProtectedRoute requiredModule={MODULE_NAMES.SUBSCRIPTIONS}>
            <DashboardLayout>
              <SubscriptionDetail />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/subscription-items/:id"
        element={
          <ProtectedRoute requiredModule={MODULE_NAMES.SUBSCRIPTIONS}>
            <DashboardLayout>
              <SubscriptionItemDetail />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/economic-indices"
        element={
          <ProtectedRoute requiredModule={MODULE_NAMES.SUBSCRIPTIONS}>
            <DashboardLayout>
              <EconomicIndicesPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/users"
        element={
          <ProtectedRoute requiredModule={MODULE_NAMES.USERS}>
            <DashboardLayout>
              <Users />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <Profile />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute requiredModule={MODULE_NAMES.SETTINGS}>
            <DashboardLayout>
              <Settings />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/subscription-plans"
        element={
          <ProtectedRoute requiredModule={MODULE_NAMES.SUBSCRIPTION_PLANS}>
            <DashboardLayout>
              <SubscriptionPlans />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/subscription-plans/new"
        element={
          <ProtectedRoute requiredModule={MODULE_NAMES.SUBSCRIPTION_PLANS}>
            <DashboardLayout>
              <NewSubscriptionPlan />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/subscription-plans/edit/:id"
        element={
          <ProtectedRoute requiredModule={MODULE_NAMES.SUBSCRIPTION_PLANS}>
            <DashboardLayout>
              <EditSubscriptionPlan />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/subscription-plans/details/:id"
        element={
          <ProtectedRoute requiredModule={MODULE_NAMES.SUBSCRIPTION_PLANS}>
            <DashboardLayout>
              <SubscriptionPlanDetails />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/subscription-plan-families"
        element={
          <ProtectedRoute requiredModule={MODULE_NAMES.SUBSCRIPTION_PLANS}>
            <DashboardLayout>
              <SubscriptionPlanFamilies />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/product-catalog"
        element={
          <ProtectedRoute requiredModule={MODULE_NAMES.SUBSCRIPTION_PLANS}>
            <DashboardLayout>
              <ProductCatalog />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/product-catalog/:id"
        element={
          <ProtectedRoute requiredModule={MODULE_NAMES.SUBSCRIPTION_PLANS}>
            <DashboardLayout>
              <ProductCatalogDetails />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/subscription-wizard/*"
        element={
          <ProtectedRoute requiredModule={MODULE_NAMES.SUBSCRIPTION_WIZARD}>
            <DashboardLayout>
              <SubscriptionWizardPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/subscription-wizard/confirmation"
        element={
          <ProtectedRoute requiredModule={MODULE_NAMES.SUBSCRIPTION_WIZARD}>
            <DashboardLayout>
              <SubscriptionConfirmation />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/subscription-confirmation/:id"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <SubscriptionConfirmation />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/subscriptions/wizard/Step1"
        element={
          <ProtectedRoute>
            <SubscriptionWizardStep1 />
          </ProtectedRoute>
        }
      />
      <Route
        path="/integrations"
        element={
          <ProtectedRoute requiredModule={MODULE_NAMES.INTEGRATIONS}>
            <DashboardLayout>
              <Integrations />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/invoices"
        element={
          <ProtectedRoute requiredModule={MODULE_NAMES.INVOICES}>
            <DashboardLayout>
              <Invoices />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/invoices/:id"
        element={
          <ProtectedRoute requiredModule={MODULE_NAMES.INVOICES}>
            <DashboardLayout>
              <InvoiceDetail />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/courses"
        element={
          <ProtectedRoute requiredModule={MODULE_NAMES.COURSES}>
            <DashboardLayout>
              <Courses />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/courses/new"
        element={
          <ProtectedRoute requiredModule={MODULE_NAMES.COURSES}>
            <DashboardLayout>
              <CourseForm />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/courses/:id"
        element={
          <ProtectedRoute requiredModule={MODULE_NAMES.COURSES}>
            <DashboardLayout>
              <CourseDetail />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/courses/:id/edit"
        element={
          <ProtectedRoute requiredModule={MODULE_NAMES.COURSES}>
            <DashboardLayout>
              <CourseForm />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/course-families"
        element={
          <ProtectedRoute requiredModule={MODULE_NAMES.COURSES}>
            <DashboardLayout>
              <CourseFamilies />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/classes"
        element={
          <ProtectedRoute requiredModule={MODULE_NAMES.FORMATIONS}>
            <DashboardLayout>
              <Classes />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/classes/:id"
        element={
          <ProtectedRoute requiredModule={MODULE_NAMES.FORMATIONS}>
            <DashboardLayout>
              <ClassDetails />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/classes/:id/sessions"
        element={
          <ProtectedRoute requiredModule={MODULE_NAMES.CLASSES}>
            <ClassDetails />
          </ProtectedRoute>
        }
      />
      <Route
        path="/classes/:id/sessions/edit/:sessionId"
        element={
          <ProtectedRoute requiredModule={MODULE_NAMES.CLASSES}>
            <ClassDetails />
          </ProtectedRoute>
        }
      />
      <Route
        path="/classes/:classId/sponsors"
        element={
          <ProtectedRoute requiredModule={MODULE_NAMES.CLASSES}>
            <ClassSponsors />
          </ProtectedRoute>
        }
      />
      <Route
        path="/class-sessions/:classId"
        element={
          <ProtectedRoute requiredModule={MODULE_NAMES.CLASSES}>
            <ClassDetails />
          </ProtectedRoute>
        }
      />
      <Route
        path="/class-sponsors"
        element={
          <ProtectedRoute requiredModule={MODULE_NAMES.CLASSES}>
            <ClassSponsors />
          </ProtectedRoute>
        }
      />
      <Route
        path="/class-details/:id"
        element={
          <ProtectedRoute requiredModule={MODULE_NAMES.CLASSES}>
            <ClassDetails />
          </ProtectedRoute>
        }
      />
      <Route
        path="/instructors"
        element={
          <ProtectedRoute requiredModule={MODULE_NAMES.INSTRUCTORS}>
            <DashboardLayout>
              <Instructors />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/class-wizard/*"
        element={
          <ProtectedRoute requiredModule={MODULE_NAMES.CLASSES}>
            <DashboardLayout>
              <ClassWizardPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/class-wizard/confirmation"
        element={
          <ProtectedRoute requiredModule={MODULE_NAMES.CLASSES}>
            <DashboardLayout>
              <ClassConfirmationPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      {/* Email routes */}
      <Route
        path="/email-templates/create"
        element={
          <ProtectedRoute requiredModule={MODULE_NAMES.EMAIL_TEMPLATES}>
            <DashboardLayout>
              <EmailTemplateCreate />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/email-templates/:id"
        element={
          <ProtectedRoute requiredModule={MODULE_NAMES.EMAIL_TEMPLATES}>
            <DashboardLayout>
              <EmailTemplateEdit />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/transactional-emails"
        element={
          <ProtectedRoute requiredModule={MODULE_NAMES.TRANSACTIONAL_EMAILS}>
            <DashboardLayout>
              <TransactionalEmails />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/templates-email"
        element={
          <ProtectedRoute requiredModule={MODULE_NAMES.EMAIL_TEMPLATES}>
            <DashboardLayout>
              <ReactEmailPreview />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/email-preview-v4"
        element={
          <ProtectedRoute requiredModule={MODULE_NAMES.EMAIL_TEMPLATES}>
            <DashboardLayout>
              <EmailPreviewV4 />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      {/* Notificações do Sistema */}
      <Route
        path="/system-notifications"
        element={
          <ProtectedRoute requiredModule={MODULE_NAMES.SYSTEM_NOTIFICATIONS}>
            <SystemNotifications />
          </ProtectedRoute>
        }
      />

      {/* Página de Controle de Acesso */}
      <Route
        path="/access-control"
        element={
          <ProtectedRoute requiredModule={MODULE_NAMES.ACCESS_CONTROL}>
            <DashboardLayout>
              <AccessControl />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />


      {/* System Jobs */}
      <Route
        path="/system-jobs"
        element={
          <ProtectedRoute requiredModule={MODULE_NAMES.SETTINGS}>
            <DashboardLayout>
              <SystemJobs />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      {/* Task Management */}
      <Route
        path="/task-management"
        element={
          <ProtectedRoute requiredModule={MODULE_NAMES.PLAYBOOKS}>
            <DashboardLayout>
              <TaskManagementPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/task-management/settings"
        element={
          <ProtectedRoute requiredModule={MODULE_NAMES.MY_ACTIVITIES}>
            <TaskSettingsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/task-management/labels"
        element={
          <ProtectedRoute requiredModule={MODULE_NAMES.MY_ACTIVITIES}>
            <TaskLabelsPage />
          </ProtectedRoute>
        }
      />

      {/* System Orders */}
      <Route
        path="/system-orders"
        element={
          <ProtectedRoute requiredModule={MODULE_NAMES.ECOMMERCE}>
            <SystemOrders />
          </ProtectedRoute>
        }
      />

      {/* Error routes */}
      <Route path="/unauthorized" element={<Unauthorized />} />
      <Route path="*" element={<NotFound />} />

      {/* New routes */}
      <Route
        path="/transactions"
        element={
          <ProtectedRoute requiredModule={MODULE_NAMES.TRANSACTIONS}>
            <DashboardLayout>
              <Transactions />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/transactions/:id"
        element={
          <ProtectedRoute requiredModule={MODULE_NAMES.TRANSACTIONS}>
            <DashboardLayout>
              <TransactionDetail />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/voucher-codes"
        element={
          <ProtectedRoute requiredModule={MODULE_NAMES.VOUCHERS}>
            <DashboardLayout>
              <VoucherCodes />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      {/* Redirects from /business/ paths to normal paths */}
      <Route
        path="/business/subscription-plans"
        element={<Navigate to="/subscription-plans" replace />}
      />
      <Route
        path="/business/subscription-plans/new"
        element={<Navigate to="/subscription-plans/new" replace />}
      />
      <Route
        path="/business/subscription-plans/edit/:id"
        element={<Navigate to="/subscription-plans/edit/:id" replace />}
      />
      <Route
        path="/business/subscription-plans/details/:id"
        element={<Navigate to="/subscription-plans/details/:id" replace />}
      />
      <Route
        path="/business/subscription-plan-families"
        element={<Navigate to="/subscription-plan-families" replace />}
      />
      <Route
        path="/business/import-subscriptions-plans"
        element={<Navigate to="/import-subscriptions-plans" replace />}
      />

      {/* Business Opportunities routes */}
      <Route
        path="/business-opportunities"
        element={
          <ProtectedRoute requiredModule={MODULE_NAMES.BUSINESS_OPPORTUNITIES}>
            <DashboardLayout>
              <BusinessOpportunities />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/business-opportunities/new"
        element={
          <ProtectedRoute requiredModule={MODULE_NAMES.BUSINESS_OPPORTUNITIES}>
            <DashboardLayout>
              <BusinessOpportunityForm />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/business-opportunities/:id/edit"
        element={
          <ProtectedRoute requiredModule={MODULE_NAMES.BUSINESS_OPPORTUNITIES}>
            <DashboardLayout>
              <BusinessOpportunityForm />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/business-opportunities/:id"
        element={
          <ProtectedRoute requiredModule={MODULE_NAMES.BUSINESS_OPPORTUNITIES}>
            <DashboardLayout>
              <BusinessOpportunityDetail />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/business-opportunities/:id/simple"
        element={
          <ProtectedRoute requiredModule={MODULE_NAMES.BUSINESS_OPPORTUNITIES}>
            <OpportunityDetailSimple />
          </ProtectedRoute>
        }
      />
      <Route
        path="/proposals/:id"
        element={
          <ProtectedRoute requiredModule={MODULE_NAMES.PROPOSAL_DETAIL}>
            <ProposalDetail />
          </ProtectedRoute>
        }
      />
      


      {/* Opportunity Wizard routes */}
      <Route
        path="/opportunity-wizard/*"
        element={
          <ProtectedRoute requiredModule={MODULE_NAMES.BUSINESS_OPPORTUNITIES}>
            <OpportunityWizardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/opportunity-wizard/confirmation/:id"
        element={
          <ProtectedRoute requiredModule={MODULE_NAMES.BUSINESS_OPPORTUNITIES}>
            <DashboardLayout>
              <OpportunityConfirmation />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/opportunity-confirmation/:id"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <OpportunityConfirmation />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      {/* Rotas de contratos */}
      <Route
        path="/contracts"
        element={
          <ProtectedRoute requiredModule={MODULE_NAMES.CONTRACTS}>
            <Contracts />
          </ProtectedRoute>
        }
      />
      <Route
        path="/contracts/:id"
        element={
          <ProtectedRoute requiredModule={MODULE_NAMES.CONTRACTS}>
            <ContractDetail />
          </ProtectedRoute>
        }
      />

      {/* Rotas para Templates de Contrato */}
      <Route
        path="/contract-templates"
        element={
          <ProtectedRoute requiredModule={MODULE_NAMES.CONTRACT_TEMPLATES}>
            <ContractTemplates />
          </ProtectedRoute>
        }
      />
      <Route
        path="/contract-templates/new"
        element={
          <ProtectedRoute requiredModule={MODULE_NAMES.CONTRACT_TEMPLATES}>
            <ContractTemplateForm />
          </ProtectedRoute>
        }
      />
      <Route
        path="/contract-templates/:id"
        element={
          <ProtectedRoute requiredModule={MODULE_NAMES.CONTRACT_TEMPLATES}>
            <ContractTemplateForm />
          </ProtectedRoute>
        }
      />

      {/* Rotas para Templates de Proposta */}
      <Route
        path="/proposal-templates"
        element={
          <ProtectedRoute requiredModule={MODULE_NAMES.PROPOSAL_TEMPLATES}>
            <ProposalTemplates />
          </ProtectedRoute>
        }
      />
      <Route
        path="/proposal-templates/new"
        element={
          <ProtectedRoute requiredModule={MODULE_NAMES.PROPOSAL_TEMPLATES}>
            <ProposalTemplateForm />
          </ProtectedRoute>
        }
      />
      <Route
        path="/proposal-templates/:id"
        element={
          <ProtectedRoute requiredModule={MODULE_NAMES.PROPOSAL_TEMPLATES}>
            <ProposalTemplateForm />
          </ProtectedRoute>
        }
      />

      {/* Nova implementação de Playbooks */}
      <Route 
        path="/playbooks" 
        element={
          <ProtectedRoute>
            <PlaybooksCustomerSuccessPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/playbooks/templates" 
        element={
          <ProtectedRoute>
            <PlaybooksCustomerSuccessPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/playbooks/templates/new" 
        element={
          <ProtectedRoute>
            <NewPlaybookTemplate />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/playbooks/templates/edit/:id" 
        element={
          <ProtectedRoute>
            <EditPlaybookTemplate />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/playbooks/runs" 
        element={
          <ProtectedRoute>
            <PlaybookRuns />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/playbooks/runs/new" 
        element={
          <ProtectedRoute>
            <NewPlaybookRun />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/playbooks/runs/:id" 
        element={
          <ProtectedRoute>
            <PlaybookRunDetail />
          </ProtectedRoute>
        }
      />

      {/* Customer Success Playbooks - Usando as páginas existentes */}
      <Route 
        path="/customer-success/playbooks" 
        element={
          <ProtectedRoute>
            <PlaybooksCustomerSuccessPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/customer-success/playbooks/runs/:id" 
        element={
          <ProtectedRoute>
            <PlaybookRunDetail />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/customer-success/playbooks/template/:id" 
        element={
          <ProtectedRoute>
            <EditPlaybookTemplate />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/customer-success/playbooks/templates" 
        element={
          <ProtectedRoute>
            <PlaybooksCustomerSuccessPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/customer-success/playbooks/edit-template/:id" 
        element={
          <ProtectedRoute>
            <EditPlaybookTemplate />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/customer-success/playbooks/runs" 
        element={
          <ProtectedRoute>
            <PlaybookRuns />
          </ProtectedRoute>
        } 
      />

      {/* Rota para exemplos de loading */}
      <Route 
        path="/loading-examples" 
        element={
          <ProtectedRoute>
            <LoadingExamples />
          </ProtectedRoute>
        } 
      />

      {/* Analytics routes */}
      <Route
        path="/analytics/mau"
        element={
          <ProtectedRoute requiredModule={MODULE_NAMES.DASHBOARD}>
            <DashboardLayout>
              <MAUAnalytics />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}

export default AppRoutes; 